import {
  setupReturnCorrelationSchema,
  readCorrelatedSetupReturn,
  clearSetupReturnParams,
} from "components/payment/PaymentMethodSetup/setupReturn";
import * as yup from "yup";
import pick from "lodash/pick";
import {
  profileSetupCorrelationSchema,
  type ProfileSetupCorrelation,
} from "components/profile/paymentMethodSetupReturn";
import type { ConfirmReservationFormState } from "./form/schemaValidation";

const STORAGE_KEY = "moovs.quotePaymentMethodSetup";
const FORM_FIELDS = [
  "driverGratuityPctCustomerInput",
  "pickUpVariantSelected",
  "autoPaymentAmt",
  "autoPaymentType",
  "promoCodeCustomerInput",
  "additonalItemsByTrip",
] as const;
type QuoteFormSnapshot = Pick<
  ConfirmReservationFormState,
  (typeof FORM_FIELDS)[number]
>;
const correlationSchema = setupReturnCorrelationSchema.shape({
  operatorId: yup.string().required(),
  requestId: yup.string().required(),
  payerId: yup.string().required(),
  ownerKey: yup.string().required(),
  expiresAt: yup.number().required(),
  form: yup.mixed<QuoteFormSnapshot>().required(),
  profileSetup: profileSetupCorrelationSchema.nullable().default(null),
});
type QuoteSetupCorrelation = yup.InferType<typeof correlationSchema>;
export type QuoteSetupReturn = QuoteSetupCorrelation & { clientSecret: string };
export type QuoteSetupSource = Pick<
  QuoteSetupCorrelation,
  "operatorId" | "requestId" | "payerId" | "ownerKey"
>;
export const matchesQuoteSetupReturn = (
  returned: QuoteSetupReturn,
  source: QuoteSetupSource
) =>
  returned.operatorId === source.operatorId &&
  returned.requestId === source.requestId &&
  returned.payerId === source.payerId &&
  returned.ownerKey === source.ownerKey;

export const retainQuoteSetup = (
  source: QuoteSetupSource,
  setupIntentId: string,
  form: ConfirmReservationFormState,
  profileSetup?: ProfileSetupCorrelation
) => {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...source,
        setupIntentId,
        returnPath: window.location.pathname,
        expiresAt: Date.now() + 30 * 60 * 1000,
        form: pick(form, FORM_FIELDS),
        profileSetup,
      })
    );
  } catch {
    // Same-page checkout can continue without redirect recovery storage.
  }
};
export const clearQuoteSetupReturn = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage cleanup must not reject a valid payment method.
  }
};

/** Capture the return secret in memory and scrub it before the order's effects run. */
export const readQuoteSetupReturn = (): QuoteSetupReturn | null => {
  const returned = readCorrelatedSetupReturn(STORAGE_KEY, correlationSchema);
  clearSetupReturnParams();
  if (!returned || returned.expiresAt <= Date.now()) return null;
  const profile = returned.profileSetup;
  if (
    profile &&
    (profile.operatorId !== returned.operatorId ||
      profile.payerId !== returned.payerId ||
      profile.setupIntentId !== returned.setupIntentId ||
      profile.returnPath !== returned.returnPath)
  )
    return null;
  return {
    ...returned,
    form: pick(returned.form, FORM_FIELDS),
  };
};
